import React, { memo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CheckCircle2, AlertCircle, FileText, File } from "lucide-react";
import FilePreviewDialog from "./FilePreviewDialog";

export const truncateFileName = (name, maxLength = 20) => {
  if (!name) return "";
  return name.length > maxLength ? name.slice(0, maxLength) + "..." : name;
};

const UploadedFilesSection = memo(({ uploadedFiles }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    file: null,
    fileName: "",
  });

  const handlePreview = (file, fileName) => {
    setPreviewDialog({
      open: true,
      file,
      fileName,
    });
  };

  const getTotalFiles = () => {
    let count = 0;
    Object.values(uploadedFiles).forEach((doc) => {
      if (doc.single) count++;
      if (doc.front) count++;
      if (doc.back) count++;
      if (Array.isArray(doc.multiple)) count += doc.multiple.length;
    });
    return count;
  };

  const renderFileItem = (file, label) => {
    if (!file) return null;

    const isImage = file.type && file.type.startsWith("image/");
    const isPDF =
      file.type === "application/pdf" ||
      file.name?.toLowerCase().endsWith(".pdf");

    return (
      <Box
        key={`${label}-${file.name}`}
        sx={{
          p: 2,
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #d1fae5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 1.5,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "#f0fdf4",
            borderColor: "#86efac",
          },
        }}
        onClick={() => handlePreview(file, file.name)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Box
            sx={{
              minWidth: "40px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {isImage ? (
              <Box
                component="img"
                src={URL.createObjectURL(file)}
                sx={{
                  width: "35px",
                  height: "35px",
                  borderRadius: "4px",
                  objectFit: "cover",
                  border: "1px solid #e5e7eb",
                }}
              />
            ) : isPDF ? (
              <FileText size={32} style={{ color: "#dc2626" }} />
            ) : (
              <File size={32} style={{ color: "#2563eb" }} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {isMobile
                ? truncateFileName(file.name, 20)
                : truncateFileName(file.name, 30)}
            </Typography>
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}
            >
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              {label && (
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    height: "20px",
                    backgroundColor: "#dbeafe",
                    color: "#0369a1",
                    fontSize: "11px",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            label={isImage ? "Image" : isPDF ? "PDF" : "File"}
            size="small"
            sx={{
              backgroundColor: isImage
                ? "#dbeafe"
                : isPDF
                ? "#fee2e2"
                : "#f3f4f6",
              color: isImage ? "#0369a1" : isPDF ? "#dc2626" : "#6b7280",
            }}
          />

          <Typography
            variant="caption"
            sx={{ color: "#059669", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            Click to preview
          </Typography>
        </Box>
      </Box>
    );
  };

  const totalFiles = getTotalFiles();

  if (totalFiles === 0) {
    return (
      <Card
        sx={{ mb: 3, backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <AlertCircle
              style={{
                fontSize: "20px",
                color: "#dc2626",
                flexShrink: 0,
                marginTop: "2px",
              }}
            />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#dc2626",
                }}
              >
                No documents uploaded
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#991b1b",
                  display: "block",
                  mt: 0.5,
                }}
              >
                Please go back to upload documents.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{ mb: 3, border: "1px solid #e5e7eb", backgroundColor: "#f0fdf4" }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "#059669",
            mb: 2.5,
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircle2 style={{ fontSize: "20px", color: "#059669" }} />
          Uploaded Documents ({totalFiles} files)
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.entries(uploadedFiles).map(([documentId, files]) => {
            const hasFiles =
              files.single ||
              files.front ||
              files.back ||
              (Array.isArray(files.multiple) && files.multiple.length > 0);

            if (!hasFiles) return null;

            return (
              <Box key={documentId}>
                {files.single && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: "#6b7280",
                        mb: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {documentId}
                    </Typography>
                    {renderFileItem(files.single, "Single")}
                  </Box>
                )}

                {(files.front || files.back) && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: "#6b7280",
                        mb: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {documentId}
                    </Typography>
                    {files.front && renderFileItem(files.front, "Front")}
                    {files.back && renderFileItem(files.back, "Back")}
                  </Box>
                )}

                {Array.isArray(files.multiple) && files.multiple.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: "#6b7280",
                        mb: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {documentId} ({files.multiple.length} files)
                    </Typography>
                    {files.multiple.map((file, idx) =>
                      renderFileItem(file, `File ${idx + 1}`)
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </CardContent>

      <FilePreviewDialog
        open={previewDialog.open}
        onClose={() =>
          setPreviewDialog({ open: false, file: null, fileName: "" })
        }
        file={previewDialog.file}
        fileName={previewDialog.fileName}
      />
    </Card>
  );
});

export default UploadedFilesSection;
